import { useEffect, useState } from 'react';
import { PointsTimeline } from './PointsTimeline';
import { Link } from '@tanstack/react-router';
import { ModuleWeight } from '@/components/ModuleWeight';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight2, Timer1 } from 'iconsax-reactjs';
import { MARKER_ZONES, formatMinute } from '@pipyar/core';
import { api } from '@/lib/api';
import { useSymbol } from '@/lib/useSymbol';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

interface NextPoint {
  id: number;
  name: string;
  color: string;
  minute: number;
  timezone: string | null;
  /** True UTC instants — no broker-shift correction needed. */
  nextTime: number;
  prevTime: number | null;
}

interface NextPointsResponse {
  symbol: string;
  serverTime: number;
  points: NextPoint[];
}

/** Countdown turns urgent inside this. */
const IMMINENT = 15 * 60;

/**
 * The time points on the dashboard: what fires next, in the header, and the
 * day's points on a timeline below it (see PointsTimeline for the drawing).
 */
export function PointsWidget() {
  const { symbol, instrument, isLoading: symbolLoading } = useSymbol();
  const { t } = useI18n();

  const query = useQuery({
    queryKey: ['next-points', symbol],
    queryFn: () =>
      api.get<NextPointsResponse>(`/api/market/points/next?symbol=${encodeURIComponent(symbol!)}`),
    enabled: Boolean(symbol),
    // The dashboard polls fast: these summaries are cheap (tens of
    // milliseconds server-side) and a board you glance at should not be showing
    // a price from a minute ago. React Query pauses the timer while the tab is
    // in the background, so this costs nothing when you are not looking.
    refetchInterval: 5_000,
    staleTime: 4_000,
  });

  const [now, setNow] = useState(() => Date.now() / 1000);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, []);

  // Same clock-skew correction as the alert and the sidebar, so every countdown
  // in the app agrees even when this machine's clock has drifted.
  const serverTime = query.data?.serverTime;
  const [skew, setSkew] = useState(0);
  useEffect(() => {
    if (serverTime !== undefined) setSkew(serverTime - Date.now() / 1000);
  }, [serverTime]);

  const clockNow = now + skew;
  const points = query.data?.points ?? [];
  const leader = points[0] ?? null;
  const loading = query.isPending || symbolLoading;

  const until = leader ? leader.nextTime - clockNow : null;
  const urgent = until !== null && until <= IMMINENT;
  const zoneOf = (p: NextPoint) =>
    p.timezone
      ? (MARKER_ZONES.find((z) => z.id === p.timezone)?.label ?? p.timezone)
      : t('points.broker');

  return (
    <Link
      to="/time-windows"
      className="group flex h-full flex-col overflow-hidden glass glass-hover rounded-2xl"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-border/60 px-3 py-2">
        <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary">
          <Timer1 size={16} variant="Bold" />
        </div>
        <h2 className="text-sm font-medium">{t('points.title')}</h2>
        <ModuleWeight id="points" />

        {/* What fires next, at a glance: the one thing this widget is for. */}
        {loading ? (
          <span className="ms-2 h-5 w-48 animate-pulse rounded bg-muted" />
        ) : leader && until !== null ? (
          <span
            className={cn(
              'ms-2 flex min-w-0 items-center gap-2 rounded-lg border px-2 py-0.5',
              urgent ? 'border-primary/50 bg-primary/10' : 'border-border/70 bg-elevated/60',
            )}
          >
            <span className="text-[10px] font-medium tracking-wide text-muted-foreground uppercase">
              {t('points.nextUp')}
            </span>
            <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: leader.color }} />
            <span className="truncate text-xs font-medium">{leader.name}</span>
            <span className={cn('tabular shrink-0 text-sm font-semibold', urgent && 'text-primary')}>
              {formatUntil(until)}
            </span>
            <span className="hidden shrink-0 text-[11px] text-muted-foreground md:inline">
              {formatMinute(leader.minute)} {zoneOf(leader)}
            </span>
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{t('points.none')}</span>
        )}

        <ArrowRight2
          size={15}
          className="ms-auto shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
        />
      </div>

      <div className="min-h-0 flex-1">
        <PointsTimeline
          points={points}
          clockNow={clockNow}
          leaderId={leader?.id ?? null}
          urgent={urgent}
          describe={(p) => {
            const full = points.find((x) => x.id === p.id);
            return full ? `${formatMinute(full.minute)} ${zoneOf(full)}` : '';
          }}
          nowLabel={t('points.now')}
          formatUntil={formatUntil}
          offsetSec={instrument?.storedGmtOffsetSec ?? 0}
        />
      </div>
    </Link>
  );
}

/** "2h 14m" far out, "14m 03s" inside the hour. */
function formatUntil(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}
